import type { NotificationInstance } from 'antd/es/notification/interface'

export type ApiErrorResponse = {
  message: string
  details?: Record<string, { error: string }[]>
}

type NormalizedApiError = {
  status: number | undefined
  message: string | undefined
  details: ApiErrorResponse['details'] | undefined
}

export const DEFAULT_API_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.'

export const normalizeApiError = (error: unknown): NormalizedApiError | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  const response = (error as { response?: { status?: number; data?: unknown } }).response
  const data = response?.data
  const isApiResponse = data && typeof data === 'object' && 'message' in data
  return {
    status: response?.status,
    message: isApiResponse ? String((data as ApiErrorResponse).message) : undefined,
    details: isApiResponse ? (data as ApiErrorResponse).details : undefined,
  }
}

export const getApiErrorMessage = (error: unknown, fallback: string): string =>
  normalizeApiError(error)?.message ?? fallback

// Single funnel for all API errors: normalizes the raw thrown value into a structured shape before logging.
export const logApiError = (context: string, error: unknown): void => {
  const normalized = normalizeApiError(error)
  if (normalized) {
    console.error(`[API Error] ${context}`, {
      status: normalized.status,
      message: normalized.message,
      ...(normalized.details && { details: normalized.details }),
    })
  } else {
    console.error(`[Error] ${context}`, error)
  }
}

const createHandler = (
  level: 'success' | 'info' | 'warning',
  logFn: (...args: unknown[]) => void,
  toast: NotificationInstance,
  context: string,
  message: string,
  description?: string
) => () => {
  logFn(`[${level.toUpperCase()}] ${context}`, message)
  toast[level]({ message, description })
}

export const makeSuccessHandler = (
  toast: NotificationInstance,
  context: string,
  message: string,
  description?: string
) => createHandler('success', console.log, toast, context, message, description)

export const makeInfoHandler = (
  toast: NotificationInstance,
  context: string,
  message: string,
  description?: string
) => createHandler('info', console.info, toast, context, message, description)

export const makeWarningHandler = (
  toast: NotificationInstance,
  context: string,
  message: string,
  description?: string
) => createHandler('warning', console.warn, toast, context, message, description)

// Factory for mutation onError callbacks. Every write operation (create, update, delete, field mutations)
// uses this so all errors are funneled through logApiError rather than handled ad-hoc at each callsite.
export const makeErrorHandler =
  (toast: NotificationInstance, context: string, message: string) =>
  (error: unknown): void => {
    logApiError(context, error)
    toast.error({
      message,
      description: getApiErrorMessage(error, DEFAULT_API_ERROR_MESSAGE),
    })
  }
